import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { Ship, Battery, Zap, Cpu, Leaf, Globe } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

type ExpertiseArea = {
  id: string;
  name: string;
  description: string;
};

const DemoCallForSolutionsQuebecFunctionalArea = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  const expertiseAreas: ExpertiseArea[] = [
    {
      id: "smart-maritime-logistics",
      name: t('quebec.expertiseArea.smartMaritimeLogistics'),
      description: t('quebec.expertiseArea.smartMaritimeLogistics.desc')
    },
    {
      id: "electrified-ground-equipment",
      name: t('quebec.expertiseArea.electrifiedGroundEquipment'),
      description: t('quebec.expertiseArea.electrifiedGroundEquipment.desc')
    },
    {
      id: "clean-battery-systems",
      name: t('quebec.expertiseArea.cleanBatterySystems'),
      description: t('quebec.expertiseArea.cleanBatterySystems.desc')
    },
    {
      id: "renewable-energy-integration",
      name: t('quebec.expertiseArea.renewableEnergyIntegration'),
      description: t('quebec.expertiseArea.renewableEnergyIntegration.desc')
    },
    {
      id: "digital-carbon-tracking",
      name: t('quebec.expertiseArea.digitalCarbonTracking'),
      description: t('quebec.expertiseArea.digitalCarbonTracking.desc')
    },
    {
      id: "cross-border-collaboration",
      name: t('quebec.expertiseArea.crossBorderCollaboration'),
      description: t('quebec.expertiseArea.crossBorderCollaboration.desc')
    }
  ];

  const handleContinue = () => {
    if (selectedArea) {
      const selectedAreaName = expertiseAreas.find(area => area.id === selectedArea)?.name || selectedArea;
      navigate("/demo-call-for-solutions-quebec/bounty", {
        state: {
          selectedArea: selectedAreaName
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/quebec-channel")}
        />
      </div>

      <Card className="w-full max-w-3xl border border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold text-center mb-6">
            {t('quebec.functionalArea.title')}
          </h2>
          
          <RadioGroup 
            value={selectedArea || ""} 
            onValueChange={setSelectedArea}
            className="space-y-4"
          >
            {expertiseAreas.map(area => (
              <div 
                key={area.id} 
                className="flex items-start space-x-2 p-2 rounded-md hover:bg-gray-50"
              >
                <RadioGroupItem value={area.id} id={area.id} className="mt-1" />
                <div className="flex-1">
                  <Label 
                    htmlFor={area.id} 
                    className="font-medium cursor-pointer flex items-center"
                  >
                    {area.name} <span className="text-gray-500 ml-2">–</span>
                  </Label>
                  <p className="text-gray-600 text-sm ml-2">{area.description}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
          
          <div className="flex justify-between mt-8">
            <Button 
              variant="outline" 
              onClick={() => navigate("/demo-call-for-solutions-quebec")}
              className="border-gray-300"
            >
              {t('ui.back')}
            </Button>
            <Button 
              onClick={handleContinue}
              disabled={!selectedArea}
              className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
            >
              {t('ui.continue')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoCallForSolutionsQuebecFunctionalArea;